import {
  collection,
  doc,
  getDocs,
  onSnapshot,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import { db } from "../services/firebase";
import { logFirebaseAccessError } from "./firebasePermissionDebug";

let activeWorkspace = null;
let unsubscribeJobs = null;
let unsubscribeRecords = null;
let reconnectHandler = null;

const PENDING_SYNC_KEY = "manucalc_pending_cloud_sync_v1";

const emitSyncStatus = (detail) => {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent("manucalc:cloud-sync-status", {
    detail: {
      workspacePath: activeWorkspace?.path?.join("/") || "",
      at: new Date().toISOString(),
      ...detail,
    },
  }));
};

const timestampValue = (value) => {
  const parsed = new Date(value || 0).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
};

const safeParse = (value, fallback = []) => {
  try {
    const parsed = JSON.parse(value || "[]");
    return Array.isArray(parsed) ? parsed : fallback;
  } catch {
    return fallback;
  }
};

const readPendingWrites = () => {
  if (typeof window === "undefined") return [];
  return safeParse(window.localStorage.getItem(PENDING_SYNC_KEY), []);
};

export const getPendingWorkspaceSyncCount = () => readPendingWrites().length;

const writePendingWrites = (items) => {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(PENDING_SYNC_KEY, JSON.stringify(items));
};

const enqueuePendingWrite = (item) => {
  const pending = readPendingWrites();
  const key = `${item.kind}:${item.type || ""}:${item.id || ""}`;
  const next = [
    ...pending.filter((existing) => `${existing.kind}:${existing.type || ""}:${existing.id || ""}` !== key),
    { ...item, queuedAt: new Date().toISOString() },
  ].slice(-500);
  writePendingWrites(next);
};

const removePendingWrite = (item) => {
  const key = `${item.kind}:${item.type || ""}:${item.id || ""}`;
  writePendingWrites(
    readPendingWrites().filter((existing) => `${existing.kind}:${existing.type || ""}:${existing.id || ""}` !== key)
  );
};

const stripLargeInlineData = (value, key = "") => {
  if (Array.isArray(value)) {
    return value.map((item) => stripLargeInlineData(item));
  }
  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value)
        .filter(([, item]) => item !== undefined)
        .map(([childKey, item]) => [childKey, stripLargeInlineData(item, childKey)])
    );
  }
  if (
    typeof value === "string" &&
    (key === "dataUrl" || key === "fileDataUrl" || value.length > 150000)
  ) {
    return "";
  }
  return value;
};

const getWorkspacePath = (user, accountProfile) => {
  const companyId =
    accountProfile?.companyId ||
    accountProfile?.companyOwnerUid ||
    "";
  if (companyId) return ["companies", companyId];
  return ["users", user.uid];
};

const getWorkspaceCollection = (name) => {
  if (!activeWorkspace) return null;
  return collection(db, ...activeWorkspace.path, name);
};

const getWorkspaceMetadata = () => ({
  ownerId: activeWorkspace?.ownerId || activeWorkspace?.uid || "",
  companyId: activeWorkspace?.companyId || "",
  workspaceScope: activeWorkspace?.companyId ? "company" : "user",
  updatedAtServer: serverTimestamp(),
});

const mergeByUpdatedAt = (localItems, cloudItems) => {
  const merged = new Map();
  [...localItems, ...cloudItems].forEach((item) => {
    if (!item?.id) return;
    const current = merged.get(item.id);
    if (!current || timestampValue(item.updatedAt) >= timestampValue(current.updatedAt)) {
      merged.set(item.id, item);
    }
  });
  return [...merged.values()].filter((item) => item.deleted !== true);
};

export const queueCloudJobSave = (job) => {
  const target = getWorkspaceCollection("manufacturingPlans");
  if (!target || !job?.id) {
    if (job?.id) enqueuePendingWrite({ kind: "jobSave", id: job.id, payload: job });
    emitSyncStatus({ status: "local", scope: "manufacturingPlans", message: "Cloud workspace is not connected yet." });
    return;
  }
  emitSyncStatus({ status: "syncing", scope: "manufacturingPlans", recordId: job.id, message: "Saving manufacturing plan..." });
  setDoc(doc(target, job.id), stripLargeInlineData({
    ...job,
    ...getWorkspaceMetadata(),
    id: job.id,
    createdBy: job.createdBy || activeWorkspace.uid,
    updatedBy: activeWorkspace.uid,
    deleted: false,
  }), { merge: true })
    .then(() => {
      removePendingWrite({ kind: "jobSave", id: job.id });
      emitSyncStatus({ status: "synced", scope: "manufacturingPlans", recordId: job.id, message: "Manufacturing plan synced." });
    })
    .catch((error) => {
    enqueuePendingWrite({ kind: "jobSave", id: job.id, payload: job });
    logFirebaseAccessError({
      error,
      path: `${activeWorkspace?.path?.join("/") || "workspace"}/manufacturingPlans/${job.id}`,
      operation: "create/update",
      user: { uid: activeWorkspace?.uid },
      context: "queueCloudJobSave",
    });
    emitSyncStatus({ status: "error", scope: "manufacturingPlans", recordId: job.id, message: "Manufacturing plan saved locally, but cloud sync failed." });
    console.warn("Manufacturing plan cloud sync failed:", error);
  });
};

export const queueCloudJobDelete = (jobId) => {
  const target = getWorkspaceCollection("manufacturingPlans");
  if (!target || !jobId) {
    if (jobId) enqueuePendingWrite({ kind: "jobDelete", id: jobId });
    emitSyncStatus({ status: "local", scope: "manufacturingPlans", message: "Cloud workspace is not connected yet." });
    return;
  }
  emitSyncStatus({ status: "syncing", scope: "manufacturingPlans", recordId: jobId, message: "Deleting manufacturing plan..." });
  setDoc(doc(target, jobId), {
    id: jobId,
    deleted: true,
    updatedAt: new Date().toISOString(),
    ...getWorkspaceMetadata(),
    updatedBy: activeWorkspace.uid,
  }, { merge: true })
    .then(() => {
      removePendingWrite({ kind: "jobDelete", id: jobId });
      emitSyncStatus({ status: "synced", scope: "manufacturingPlans", recordId: jobId, message: "Manufacturing plan delete synced." });
    })
    .catch((error) => {
      enqueuePendingWrite({ kind: "jobDelete", id: jobId });
      logFirebaseAccessError({
        error,
        path: `${activeWorkspace?.path?.join("/") || "workspace"}/manufacturingPlans/${jobId}`,
        operation: "update",
        user: { uid: activeWorkspace?.uid },
        context: "queueCloudJobDelete",
      });
      emitSyncStatus({ status: "error", scope: "manufacturingPlans", recordId: jobId, message: "Manufacturing plan deleted locally, but cloud sync failed." });
      console.warn("Manufacturing plan cloud delete failed:", error);
    });
};

export const queueCloudRecordSave = (type, record) => {
  const target = getWorkspaceCollection("workspaceRecords");
  if (!target || !type || !record?.id) {
    if (type && record?.id) enqueuePendingWrite({ kind: "recordSave", type, id: record.id, payload: record });
    emitSyncStatus({ status: "local", scope: "workspaceRecords", recordType: type, message: "Cloud workspace is not connected yet." });
    return;
  }
  const cloudId = `${type}--${record.id}`;
  emitSyncStatus({ status: "syncing", scope: "workspaceRecords", recordType: type, recordId: record.id, message: "Saving shop record..." });
  setDoc(
    doc(target, cloudId),
    stripLargeInlineData({
      ...record,
      ...getWorkspaceMetadata(),
      id: record.id,
      recordType: type,
      createdBy: record.createdBy || activeWorkspace.uid,
      updatedBy: activeWorkspace.uid,
      deleted: false,
    }),
    { merge: true }
  )
    .then(() => {
      removePendingWrite({ kind: "recordSave", type, id: record.id });
      emitSyncStatus({ status: "synced", scope: "workspaceRecords", recordType: type, recordId: record.id, message: "Shop record synced." });
    })
    .catch((error) => {
    enqueuePendingWrite({ kind: "recordSave", type, id: record.id, payload: record });
    logFirebaseAccessError({
      error,
      path: `${activeWorkspace?.path?.join("/") || "workspace"}/workspaceRecords/${cloudId}`,
      operation: "create/update",
      user: { uid: activeWorkspace?.uid },
      context: "queueCloudRecordSave",
    });
    emitSyncStatus({ status: "error", scope: "workspaceRecords", recordType: type, recordId: record.id, message: "Record saved locally, but cloud sync failed." });
    console.warn("Shop record cloud sync failed:", error);
  });
};

export const queueCloudRecordDelete = (type, recordId) => {
  const target = getWorkspaceCollection("workspaceRecords");
  if (!target || !type || !recordId) {
    if (type && recordId) enqueuePendingWrite({ kind: "recordDelete", type, id: recordId });
    emitSyncStatus({ status: "local", scope: "workspaceRecords", recordType: type, message: "Cloud workspace is not connected yet." });
    return;
  }
  emitSyncStatus({ status: "syncing", scope: "workspaceRecords", recordType: type, recordId, message: "Deleting shop record..." });
  setDoc(doc(target, `${type}--${recordId}`), {
    id: recordId,
    recordType: type,
    deleted: true,
    updatedAt: new Date().toISOString(),
    ...getWorkspaceMetadata(),
    updatedBy: activeWorkspace.uid,
  }, { merge: true })
    .then(() => {
      removePendingWrite({ kind: "recordDelete", type, id: recordId });
      emitSyncStatus({ status: "synced", scope: "workspaceRecords", recordType: type, recordId, message: "Shop record delete synced." });
    })
    .catch((error) => {
      enqueuePendingWrite({ kind: "recordDelete", type, id: recordId });
      logFirebaseAccessError({
        error,
        path: `${activeWorkspace?.path?.join("/") || "workspace"}/workspaceRecords/${type}--${recordId}`,
        operation: "update",
        user: { uid: activeWorkspace?.uid },
        context: "queueCloudRecordDelete",
      });
      emitSyncStatus({ status: "error", scope: "workspaceRecords", recordType: type, recordId, message: "Record deleted locally, but cloud sync failed." });
      console.warn("Shop record cloud delete failed:", error);
    });
};

export const stopWorkspaceCloudSync = () => {
  unsubscribeJobs?.();
  unsubscribeRecords?.();
  if (reconnectHandler && typeof window !== "undefined") {
    window.removeEventListener("online", reconnectHandler);
  }
  unsubscribeJobs = null;
  unsubscribeRecords = null;
  reconnectHandler = null;
  emitSyncStatus({ status: "local", message: "Cloud workspace sync stopped." });
  activeWorkspace = null;
};

const flushPendingWrites = () => {
  const pending = readPendingWrites();
  if (!pending.length || !activeWorkspace) return;
  emitSyncStatus({ status: "syncing", scope: "workspace", message: `Syncing ${pending.length} queued changes...` });
  pending.forEach((item) => {
    if (item.kind === "jobSave") queueCloudJobSave(item.payload);
    if (item.kind === "jobDelete") queueCloudJobDelete(item.id);
    if (item.kind === "recordSave") queueCloudRecordSave(item.type, item.payload);
    if (item.kind === "recordDelete") queueCloudRecordDelete(item.type, item.id);
  });
};

export const startWorkspaceCloudSync = async ({
  user,
  accountProfile,
  getLocalJobs,
  mergeCloudJobs,
  getRecordTypes,
  getLocalRecords,
  mergeCloudRecords,
}) => {
  stopWorkspaceCloudSync();
  if (!user?.uid) return () => {};

  activeWorkspace = {
    path: getWorkspacePath(user, accountProfile),
    uid: user.uid,
    ownerId: user.uid,
    companyId: accountProfile?.companyId || accountProfile?.companyOwnerUid || "",
  };
  if (typeof window !== "undefined") {
    reconnectHandler = () => {
      const pendingCount = readPendingWrites().length;
      if (!pendingCount) return;
      emitSyncStatus({ status: "syncing", scope: "workspace", message: `Connection restored. Retrying ${pendingCount} queued workspace changes...` });
      flushPendingWrites();
    };
    window.addEventListener("online", reconnectHandler);
  }
  emitSyncStatus({ status: "syncing", scope: "workspace", message: "Connecting workspace sync..." });

  const jobsCollection = getWorkspaceCollection("manufacturingPlans");
  const recordsCollection = getWorkspaceCollection("workspaceRecords");

  let jobsSnapshot;
  let recordsSnapshot;
  try {
    [jobsSnapshot, recordsSnapshot] = await Promise.all([
      getDocs(jobsCollection),
      getDocs(recordsCollection),
    ]);
  } catch (error) {
    logFirebaseAccessError({
      error,
      path: `${activeWorkspace.path.join("/")}/manufacturingPlans|workspaceRecords`,
      operation: "read",
      user,
      role: accountProfile?.role,
      companyId: accountProfile?.companyId || accountProfile?.companyOwnerUid,
      context: "startWorkspaceCloudSync initial load",
    });
    emitSyncStatus({ status: "error", scope: "workspace", message: "Cloud workspace could not load. Local data is still available." });
    throw error;
  }

  const cloudJobs = jobsSnapshot.docs.map((item) => ({ id: item.id, ...item.data() }));
  const localJobs = getLocalJobs();
  const mergedJobs = mergeByUpdatedAt(localJobs, cloudJobs);
  mergeCloudJobs(mergedJobs);
  mergedJobs.forEach((job) => queueCloudJobSave(job));

  const cloudRecordsByType = {};
  recordsSnapshot.docs.forEach((item) => {
    const data = item.data();
    if (!data.recordType) return;
    if (!cloudRecordsByType[data.recordType]) cloudRecordsByType[data.recordType] = [];
    cloudRecordsByType[data.recordType].push({ id: data.id, ...data });
  });

  getRecordTypes().forEach((type) => {
    const merged = mergeByUpdatedAt(
      getLocalRecords(type),
      cloudRecordsByType[type] || []
    );
    mergeCloudRecords(type, merged);
    merged.forEach((record) => queueCloudRecordSave(type, record));
  });

  unsubscribeJobs = onSnapshot(jobsCollection, (snapshot) => {
    const cloud = snapshot.docs.map((item) => ({ id: item.id, ...item.data() }));
    mergeCloudJobs(mergeByUpdatedAt(getLocalJobs(), cloud));
    emitSyncStatus({ status: "synced", scope: "manufacturingPlans", message: "Manufacturing plans are synced." });
  }, (error) => {
    logFirebaseAccessError({
      error,
      path: `${activeWorkspace?.path?.join("/") || "workspace"}/manufacturingPlans`,
      operation: "listen",
      user,
      role: accountProfile?.role,
      companyId: accountProfile?.companyId || accountProfile?.companyOwnerUid,
      context: "manufacturingPlans snapshot",
    });
    emitSyncStatus({ status: "error", scope: "manufacturingPlans", message: "Manufacturing plan live sync stopped." });
  });

  unsubscribeRecords = onSnapshot(recordsCollection, (snapshot) => {
    const byType = {};
    snapshot.docs.forEach((item) => {
      const data = item.data();
      if (!data.recordType) return;
      if (!byType[data.recordType]) byType[data.recordType] = [];
      byType[data.recordType].push({ id: data.id, ...data });
    });
    Object.entries(byType).forEach(([type, cloud]) => {
      mergeCloudRecords(type, mergeByUpdatedAt(getLocalRecords(type), cloud));
    });
    emitSyncStatus({ status: "synced", scope: "workspaceRecords", message: "Shop records are synced." });
  }, (error) => {
    logFirebaseAccessError({
      error,
      path: `${activeWorkspace?.path?.join("/") || "workspace"}/workspaceRecords`,
      operation: "listen",
      user,
      role: accountProfile?.role,
      companyId: accountProfile?.companyId || accountProfile?.companyOwnerUid,
      context: "workspaceRecords snapshot",
    });
    emitSyncStatus({ status: "error", scope: "workspaceRecords", message: "Shop record live sync stopped." });
  });

  emitSyncStatus({ status: "synced", scope: "workspace", message: "Workspace sync connected." });
  flushPendingWrites();

  return stopWorkspaceCloudSync;
};

export const mergeWorkspaceRecordsByUpdatedAt = mergeByUpdatedAt;
